interface IBrowserHelper {
    hasPdfPlugin: () => boolean;
}

declare const ActiveXObject: any;

class IEHelper implements IBrowserHelper {
    hasPdfPlugin(): boolean {
        let classIds = ['AcroPDF.PDF.1', 'PDF.PdfCtrl.6', 'PDF.PdfCtrl.5'];

        for (let classId of classIds) {
            try {
                let plugin = new ActiveXObject(classId);

                if (plugin) {
                    return plugin !== null;
                }
            } catch (ex) {
                // Do nothing.
            }
        }

        return false;
    }
}

class FirefoxHelper implements IBrowserHelper {
    hasPdfPlugin(): boolean {
        let matches = /Firefox[/\s](\d+\.\d+)/.exec(navigator.userAgent);

        if (matches !== null && matches.length > 1) {
            let version = parseFloat(matches[1]);
            if (version >= 19.0) {
                // Firefox version 19.0 starts using its native pdf viewer which does not support the 'print' javascript.
                return false;
            }
        }

        let pdfPlugins = (navigator.mimeTypes as any)['application/pdf'];
        let pdfPlugin = pdfPlugins !== null ? pdfPlugins.enabledPlugin : null;

        if (pdfPlugin) {
            let description = pdfPlugin.description;

            return (
                description.indexOf('Adobe') !== -1 &&
                (description.indexOf('Version') === -1 || parseFloat(description.split('Version')[1]) >= 6)
            );
        }

        return false;
    }
}

class ChromiumHelper implements IBrowserHelper {
    private defaultPlugin: string;

    constructor(defaultPlugin: string) {
        this.defaultPlugin = defaultPlugin;
    }

    hasPdfPlugin(): boolean {
        for (let plugin of (navigator.plugins as any)) {
            if (plugin.name === this.defaultPlugin || plugin.name === 'Adobe Acrobat') {
                return true;
            }
        }

        return false;
    }
}

class OtherBrowserHelper implements IBrowserHelper {
    hasPdfPlugin(): boolean {
        return false;
    }
}

function getBrowserHelper(): IBrowserHelper {
    if (window.navigator) {
        let userAgent = window.navigator.userAgent.toLowerCase();
        if (userAgent.indexOf('msie') > -1 || (userAgent.indexOf('mozilla') > -1 && userAgent.indexOf('trident') > -1))
            return new IEHelper();
        else if (userAgent.indexOf('firefox') > -1) return new FirefoxHelper();
        else if (userAgent.indexOf('edg/') > -1) return new ChromiumHelper('Microsoft Edge PDF Plugin');
        else if (userAgent.indexOf('chrome') > -1) return new ChromiumHelper('Chrome PDF Viewer');
        else if (userAgent.indexOf('safari') > -1) return new ChromiumHelper('WebKit built-in PDF');
        return new OtherBrowserHelper();
    }

    return new OtherBrowserHelper();
}

function isSameOriginUrl(url: string): boolean {
    let location = window.location;
    let anchor = document.createElement('a');
    anchor.setAttribute('href', url);

    // IE doesn't populate all link properties when setting .href with a relative URL,
    // however .href will return an absolute URL which then can be used on itself
    // to populate these additional fields.
    if (anchor.host == '') {
        // eslint-disable-next-line no-self-assign
        anchor.href = anchor.href;
    }

    return (
        location.hostname === anchor.hostname && location.protocol === anchor.protocol && location.port === anchor.port
    );
}

function useMsBlobHandling(): boolean {
    return window.navigator && (window.navigator as any).msSaveOrOpenBlob;
}

export class PrintManager {
    private hasPdfPlugin: boolean = false;
    private isMobile: boolean;
    private iframe: HTMLIFrameElement | null = null;

    constructor() {
        this.hasPdfPlugin = getBrowserHelper().hasPdfPlugin();

        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    destroy() {
        this.iframe = null;
    }

    private printDesktop(src: string) {
        let sameOriginUrl: string | null = null;

        if (!this.iframe) {
            this.iframe = document.createElement('iframe');
            this.iframe.style.display = 'none';
            this.iframe.onload = () => {
                try {
                    this.iframe?.contentDocument?.execCommand('print', true);
                } finally {
                    if (sameOriginUrl) {
                        (window.URL || window.webkitURL).revokeObjectURL(sameOriginUrl);
                    }
                }
            };
        }

        if (isSameOriginUrl(src) && useMsBlobHandling()) {
            this.iframe.src = src;
            document.body.appendChild(this.iframe);

            return;
        }

        let request = new XMLHttpRequest();
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let that = this;
        request.open('GET', src, true);
        request.responseType = 'arraybuffer';
        request.onload = function() {
            if (this.status === 200) {
                let localPdf = new Blob([this.response], { type: 'application/pdf' });
                if (useMsBlobHandling()) {
                    (window.navigator as any).msSaveOrOpenBlob(localPdf);
                } else {
                    sameOriginUrl = (window.URL || window.webkitURL).createObjectURL(localPdf);

                    if (that.iframe != null) {
                        that.iframe.src = sameOriginUrl;
                        document.body.appendChild(that.iframe);
                    }
                }
            } else {
                // eslint-disable-next-line no-console
                console.log('Could not retrieve remote PDF document.');
            }
        };

        request.send();
    }

    private printMobile(src: string) {
        window.open(src, '_self');
    }

    print(src: string) {
        if (this.isMobile) {
            this.printMobile(src);
        } else {
            this.printDesktop(src);
        }
    }

    getDirectPrintState(): boolean {
        return this.hasPdfPlugin;
    }
}
